import React, { ReactNode, useEffect, useState } from 'react';
import { Button, Result, Spin } from 'antd';
import { ReactComponent as Loader } from '../../assets/icons/loader.svg';

const enum ResultStatus { PENDING, ACQUIRED, DENIED }

interface RequestLockResult {
	status: ResultStatus;
	error: Error | null;
}

async function requestSingleInstaceLock(onResult: (acquired: boolean, error?: Error) => void) {
	try {
		// pre-requisite: if navigator.locks is not available, we can't do anything
		if ('locks' in navigator === false) {
			const error = new Error('navigator.locks is not available in this browser.');
			onResult(true, error);
			return;
		}

		await navigator.locks.request(
			'single-instance',
			{ mode: 'exclusive', ifAvailable: true, steal: false },
			(lock): Promise<void> => {
				if (lock === null) {
					onResult(false);
					return Promise.resolve();
				}
				onResult(true);
				return new Promise(() => { });
			},
		);
	} catch (ex) {
		const error = ex instanceof Error ? ex : new Error(String(ex));
		onResult(false, error);
	}
}

const lockResultPromise = new Promise<RequestLockResult>((resolve) => {
	function onLoad() {
		setTimeout(() => {
			requestSingleInstaceLock((acquired, error) => {
				if (error) {
					console.log('Error in acquiring lock:', error);
				}
				resolve({
					status: acquired ? ResultStatus.ACQUIRED : ResultStatus.DENIED,
					error: error || null,
				});
			});
		}, 2000);
	}

	if (document.readyState !== 'complete') {
		onLoad();
		return;
	}
	window.addEventListener('load', onLoad, { once: true });
});

function useSingleInstanceLock() {
	const [result, setResult] = useState<RequestLockResult>({
		status: ResultStatus.PENDING,
		error: null,
	});

	useEffect(() => {
		lockResultPromise.then(setResult);
	}, []);
	return result;
}

function SingleInstanceLockDenied() {
	return (
		<div style={{
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			height: '100vh',
		}}
		>
			<Result
				status="info"
				title="Already open in another window or tab"
				subTitle="Close the other instance to continue here."
				extra={<Button onClick={() => window.location.reload()}>Retry</Button>}
			/>
		</div>
	);
}

export function SingleInstanceLock({ children }: { children?: ReactNode }): JSX.Element {
	const singleInstanceLock = useSingleInstanceLock();
	switch (singleInstanceLock.status) {
		case ResultStatus.ACQUIRED:
			return <>{children}</>;
		case ResultStatus.DENIED:
			return <SingleInstanceLockDenied />;
		default:
			return (
				<div style={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					height: '100vh',
				}}
				>
					<Spin
						spinning
						indicator={<Loader style={{ fontSize: '120px' }} />}
						size="large"
					/>
				</div>
			);
	}
}
